"use no memo";

import type { CellContext, Column, ColumnDef } from "@tanstack/react-table";
import type { GetAppStateResponse } from "deso-protocol";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import type { TokenBalance } from "@/graphql/codegen/graphql";
import {
  BONDING_CURVE_SHORTHAND_DISPLAY_NAME,
  DESO_TOKEN_DISPLAY_NAME,
} from "@/utils/constants";
import { desoToUSD, nanosToInteger } from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";

import { renderSortingHeader } from "../table-sorting-headers";
import { renderCoinUserTableCell } from "../trending-coins/trending-coins-cell-content";
import CreatorCoinV1Actions from "./creator-coins-v1-table-actions";

interface ColumnProps {
  appState: GetAppStateResponse | null;
}

interface CustomCellContext extends CellContext<TokenBalance, unknown> {
  pageOffset?: number;
}

export const tableColumns = ({
  appState,
}: ColumnProps): ColumnDef<TokenBalance>[] => {
  return [
    {
      accessorKey: "id",
      header: () => (
        <span className="text-xs text-muted" key="header-id">
          #
        </span>
      ),
      cell: (props) => {
        const { row, pageOffset } = props as CustomCellContext;
        return (
          <span className="text-xs text-muted">
            {+row.id + (pageOffset || 0) + 1}
          </span>
        );
      },
    },
    {
      accessorKey: "creator.publicKey",
      header: () => (
        <span className="text-xxs" key="header-username">
          Username
        </span>
      ),
      cell: ({ row }: CustomCellContext) => {
        const { username = "", publicKey = "" } = row.original.creator || {};

        return renderCoinUserTableCell({
          coinUsername: username ?? "",
          coinPublicKey: publicKey ?? "",
          clickable: true,
          coinCategory: `${BONDING_CURVE_SHORTHAND_DISPLAY_NAME} Token`,
        });
      },
    },
    {
      accessorKey: "priceUsd",
      header: () => (
        <span className="text-xxs" key="header-price">
          Price
        </span>
      ),
      cell: ({ row }: CustomCellContext) => {
        if (!appState) return 0;

        return (
          <div className="flex flex-col gap-0 text-left">
            <div className="flex items-center font-mono text-muted-foreground">
              ~
              <CoinPriceFormatter
                price={desoToUSD(
                  nanosToInteger(row.original.creator?.coinPriceDesoNanos || 0),
                  appState.USDCentsPerDeSoExchangeRate,
                )}
                highPriceThreshold={10}
                className="font-mono font-semibold text-foreground"
              />
            </div>
            <div
              className="
                font-mono text-xs text-muted
                hover:text-foreground
              "
            >
              {abbreviateNumber(
                nanosToInteger(row.original.creator?.coinPriceDesoNanos || 0),
              )}{" "}
              <span className="text-[10px]">{DESO_TOKEN_DISPLAY_NAME}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "balanceNanos",
      header: ({ column }: { column: Column<TokenBalance, unknown> }) =>
        renderSortingHeader({
          column,
          title: "Balance",
          className: "text-xxs",
        }),
      cell: ({ row }: CustomCellContext) => (
        <div className="flex flex-col gap-1 text-left text-xs">
          <CoinPriceFormatter
            price={nanosToInteger(row.original.balanceNanos)}
            isUsd={false}
            highPriceThreshold={10}
            className="font-mono font-semibold text-foreground"
          />
        </div>
      ),
    },
    {
      accessorKey: "creator.coinPriceDesoNanos",
      header: () => (
        <span className="text-xxs" key="header-value">
          Value
        </span>
      ),
      cell: ({ row }: CustomCellContext) => {
        if (!appState) return 0;
        return (
          <div className="flex flex-col gap-1 text-left text-xs">
            <CoinPriceFormatter
              price={
                nanosToInteger(row.original.balanceNanos) *
                desoToUSD(
                  nanosToInteger(row.original.creator?.coinPriceDesoNanos ?? 0),
                  appState.USDCentsPerDeSoExchangeRate,
                )
              }
              highPriceThreshold={10}
              className="font-mono font-semibold text-foreground"
            />
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }: CustomCellContext) => (
        <CreatorCoinV1Actions userName={row.original.creator?.username || ""} />
      ),
    },
  ];
};
